import { useMemo } from 'react';
import { PlanFamily } from 'src/types';
import { useSubscriptionInfo } from './useSubscriptionInfo';
import { OfferType } from 'src/types/wallet/models/OfferType';
import { useGetUser } from '../userHooks';

export type OfferedMonthsContext = 'accept' | 'reject';

type UseOfferedMonthsParams = {
  context: OfferedMonthsContext;
  offerType?: OfferType | string;
};

/**
 * Calculates how many complimentary months to offer based on the user's current plan and flow context.
 *
 * Mapping (monthly pricing in minor units, e.g., cents for USD):
 * - Accept (complimentary):
 *   - PRO: 3 months
 *   - MAX: $25 → 3, $50 → 2, $100 → 1
 *   - BUSINESS: 0 (no offer)
 * - Reject (cancel offer):
 *   - PRO: 2 months
 *   - MAX: $25 → 2, $50/$100 → 1
 *   - BUSINESS: 0 (no offer)
 */
export const useOfferedMonths = ({
  context,
  offerType,
}: UseOfferedMonthsParams) => {
  const { subscriptionInfo } = useSubscriptionInfo();
  const { isTeamsTier } = useGetUser();

  const { months, hasOffer } = useMemo(() => {
    // Try to parse months from offerType like "three_months_free_subscription_extension"
    const parseMonthsFromOfferType = (
      input?: OfferType | string,
    ): number | null => {
      if (!input) return null;
      const firstToken = String(input).toLowerCase().split('_')[0];
      const wordToNumber: Record<string, number> = {
        one: 1,
        two: 2,
        three: 3,
      };
      return wordToNumber[firstToken] ?? null;
    };

    const monthsFromOfferType = parseMonthsFromOfferType(offerType);
    if (monthsFromOfferType !== null) {
      return { months: monthsFromOfferType, hasOffer: monthsFromOfferType > 0 };
    }

    const planFamily =
      subscriptionInfo?.plan_family ||
      (isTeamsTier ? PlanFamily.BUSINESS : PlanFamily.PRO); // Note: legacy fallback
    const monthlyAmount = subscriptionInfo?.price_info?.amount ?? 0;

    // Helper to bucket MAX plan price points (25/50/100 -> minor units)
    const isMax25 = monthlyAmount > 0 && monthlyAmount <= 25;
    const isMax50 = monthlyAmount > 25 && monthlyAmount <= 50;
    const isMax100 = monthlyAmount > 50; // assume anything above 5000 maps to $100 tier

    // Default: no offer
    let calculatedMonths = 0;

    switch (planFamily) {
      case PlanFamily.BUSINESS:
        calculatedMonths = 0;
        break;
      case PlanFamily.PRO:
        calculatedMonths = context === 'accept' ? 3 : 2;
        break;
      case PlanFamily.MAX:
        if (context === 'accept') {
          if (isMax25) calculatedMonths = 3;
          else if (isMax50) calculatedMonths = 2;
          else if (isMax100) calculatedMonths = 1;
        } else {
          // reject
          if (isMax25) calculatedMonths = 2;
          else if (isMax50 || isMax100) calculatedMonths = 1;
        }
        break;
    }

    return { months: calculatedMonths, hasOffer: calculatedMonths > 0 };
  }, [
    context,
    offerType,
    subscriptionInfo?.plan_family,
    subscriptionInfo?.price_info?.amount,
    isTeamsTier,
  ]);

  return { months, hasOffer };
};
